<template>
  <div class="language-switcher">
    <div class="dropdown">
      <button @click="toggleDropdown" class="dropdown-trigger" type="button">
        <img :src="currentFlag" class="flag-icon" alt="Current language" />
        <span class="language-name">{{ getLanguageName(currentLang) }}</span>
        <span class="dropdown-arrow">▼</span>
      </button>

      <transition name="dropdown-fade">
        <div v-if="isOpen" class="dropdown-content">
          <div
            v-for="(flag, langCode) in flags"
            :key="langCode"
            class="dropdown-option"
            :class="{ active: currentLang === langCode }"
            @click="selectLanguage(langCode)"
          >
            <img :src="flag" class="flag-icon" :alt="getLanguageName(langCode)" />
            <span class="language-name">{{ getLanguageName(langCode) }}</span>
          </div>
        </div>
      </transition>
    </div>

    <!-- Backdrop to close dropdown when clicking outside -->
    <div v-if="isOpen" class="dropdown-backdrop" @click="closeDropdown"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

// Import flag images
import enFlag from "@/assets/flags/english.png";
import myFlag from "@/assets/flags/myanmar.png";
import thFlag from "@/assets/flags/thai.png";
import zhFlag from "@/assets/flags/china.png";

const { locale } = useI18n();

// Reactive state
const isOpen = ref(false);
const currentLang = ref(localStorage.getItem("lang") || "en");

// Flags configuration
const flags = {
  en: enFlag,
  my: myFlag,
  th: thFlag,
  zh: zhFlag,
};

// Computed properties
const currentFlag = computed(() => flags[currentLang.value] || flags.en);

// Language names mapping
const languageNames = {
  en: "English",
  my: "Myanmar",
  th: "Thai",
  zh: "Chinese",
};

// Methods
const toggleDropdown = (event) => {
  console.log("Toggle dropdown clicked, current state:", isOpen.value);
  event.stopPropagation();
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  console.log("Closing dropdown");
  isOpen.value = false;
};

const selectLanguage = (langCode) => {
  console.log("Selecting language:", langCode);

  if (currentLang.value !== langCode) {
    currentLang.value = langCode;
    locale.value = langCode;
    localStorage.setItem("lang", langCode);

    console.log("Language changed to:", langCode);
  }

  closeDropdown();
};

const getLanguageName = (code) => {
  return languageNames[code] || "English";
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  const dropdownElement = event.target.closest(".language-switcher");
  if (!dropdownElement) {
    closeDropdown();
  }
};

// Keyboard navigation
const handleEscapeKey = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  console.log("LanguageSwitcher mounted, current lang:", currentLang.value);
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleEscapeKey);

  // Initialize i18n locale
  if (currentLang.value) {
    locale.value = currentLang.value;
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleEscapeKey);
});
</script>

<style scoped>
.language-switcher {
  position: relative;
  display: inline-block;
  z-index: 100000; /* Very high z-index to ensure it's on top */
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px; /* Increased to accommodate text */
  z-index: 100003;
}

.dropdown-trigger:hover {
  border-color: #c0c4cc;
  background: #f5f7fa;
}

.flag-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.dropdown-trigger .language-name {
  flex: 1;
  text-align: left;
  font-size: 14px;
  color: #606266;
}

.dropdown-arrow {
  font-size: 10px;
  color: #606266;
  transition: transform 0.3s ease;
}

.dropdown-content {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  margin-top: 4px;
  z-index: 100002; /* Even higher than the container */
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: #606266;
}

.dropdown-option:hover {
  background-color: #f5f7fa;
}

.dropdown-option.active {
  background-color: #ecf5ff;
  color: #409eff;
}

.dropdown-option .language-name {
  flex: 1;
}

.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100001; /* Between container and dropdown */
  background: transparent;
}

/* Dropdown animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.3s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
